import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAtomValue } from "jotai";
import Loader from "./Loader";
import { get } from "../server";
import { PRIORITY, TEAMDETAIL } from "../utils/constants/apiUrl";
import { loggedInUser, teamIDCheck } from "../utils/store";

const FunctionVisibilityGuard = ({ functionType, children }) => {
  const loggedUser = useAtomValue(loggedInUser);
  const selectedTeamId = useAtomValue(teamIDCheck);
  const isTeamOwnerOrAdmin =
    loggedUser?.role?.includes("team_owner") ||
    loggedUser?.role?.includes("team_admin");
  const isTeamMember =
    (loggedUser?.connected_teams?.length ?? 0) > 0 && !isTeamOwnerOrAdmin;
  const teamId =
    selectedTeamId ||
    loggedUser?.connected_teams?.[0]?.id ||
    loggedUser?.connected_teams?.[0]?.team_id;
  const [allowed, setAllowed] = useState(!isTeamMember);
  const [loading, setLoading] = useState(isTeamMember);

  useEffect(() => {
    let active = true;

    const checkVisibility = async () => {
      if (!isTeamMember) {
        setAllowed(true);
        setLoading(false);
        return;
      }

      if (!teamId) {
        setAllowed(false);
        setLoading(false);
        return;
      }

      setLoading(true);
      const [response, teamResponse] = await Promise.all([
        get(`${PRIORITY}?team_id=${teamId}`, loggedUser?.token || null),
        get(`${TEAMDETAIL}/${teamId}`, loggedUser?.token || null),
      ]);
      if (!active) return;

      const currentMember = teamResponse?.status
        ? teamResponse.data?.team_members?.find(
            (item) =>
              String(item.user_id ?? item.user_data?.id) ===
              String(loggedUser?.id)
          )
        : null;
      if (
        currentMember?.is_admin === true ||
        Number(currentMember?.is_admin) === 1
      ) {
        setAllowed(true);
        setLoading(false);
        return;
      }

      if (!response?.status || !Array.isArray(response.data)) {
        setAllowed(false);
        setLoading(false);
        return;
      }

      const settings = response.data;
      const setting = settings.find(
        (item) => item.function_type === functionType
      );

      // Existing teams without a row remain enabled by default. If a row is
      // present, both the owner's permission and the member's preference must
      // be enabled before the route can be opened.
      setAllowed(
        Boolean(
          setting
            ? Number(setting.is_member_visible) === 1 &&
                Number(setting.is_visible) === 1
            : true
        )
      );
      setLoading(false);
    };

    checkVisibility();
    return () => {
      active = false;
    };
  }, [functionType, isTeamMember, loggedUser?.token, teamId]);

  if (loading) return <Loader />;
  return allowed ? children : <Navigate to="/function" replace />;
};

export default FunctionVisibilityGuard;
